import { FC, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { getRouteApi } from '@mint/routing';

import { IconButton, Popover, PopoverItem, Tooltip } from '@myn/mui';

import { invalidateBuyingPlanInfo } from '../../../api/mediaPlansApiSlice.js';
import { useSetBudgetRows } from '../../../hooks/useBudgetRows.js';
import { useBuyingPlanListSelector } from '../../../hooks/useGetBuyingPlanListData.js';
import { useIsArchivedVersion } from '../../../hooks/useIsArchivedVersion.js';
import { useSetSelectedBuyingPlanId } from '../../../hooks/useSelectedBuyingPlanId.js';

type Props = {
  selectedPlanId: number | null;
  editingDisabled: boolean;
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const MediaPlanSelect: FC<Props> = ({ selectedPlanId, editingDisabled }) => {
  const { t } = useTranslation('@myn/planning');

  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const navigate = routeApi.useNavigate();

  const mediaPlans = useBuyingPlanListSelector() ?? [];
  const setSelectedPlanId = useSetSelectedBuyingPlanId();
  const seBudgetRows = useSetBudgetRows();
  const [isArchivedVersion, setIsArchivedVersion] = useIsArchivedVersion();

  const isPopoverOpened = Boolean(anchorElement);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorElement(null);
  };

  const handleResetSearchParams = async () => {
    await navigate({ search: undefined });
  };

  const handleChangeOption = async (mediaPlanId: number) => {
    if (selectedPlanId === mediaPlanId) {
      handlePopoverClose();
      return;
    }

    await handleResetSearchParams();

    if (isArchivedVersion) {
      invalidateBuyingPlanInfo();
      setIsArchivedVersion(false);
    }

    setSelectedPlanId(mediaPlanId);
    seBudgetRows(null);
    handlePopoverClose();
  };

  return (
    <>
      <Tooltip title={t('Select Buying Plan')}>
        <IconButton
          data-testid="change-media-plan"
          aria-label={
            isPopoverOpened ? 'Hide list of plans' : 'Show list of plans'
          }
          variant="icon"
          color="primary"
          size="small"
          icon={isPopoverOpened ? 'ChevronUp' : 'ChevronDown'}
          onClick={handlePopoverOpen}
          disabled={editingDisabled}
        />
      </Tooltip>
      <Popover
        open={isPopoverOpened}
        onClose={handlePopoverClose}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {mediaPlans.map(({ id, name }) => (
          <PopoverItem
            key={id}
            aria-label="Select option"
            text={name}
            onClick={() => handleChangeOption(id)}
            selected={selectedPlanId === id}
            size="large"
            sx={{ width: '253px' }}
          />
        ))}
      </Popover>
    </>
  );
};

export default MediaPlanSelect;
